import type { DataService } from './api';
import type {
  Worker, Admin, DailyCheckin, CheckinInput, Alert, AlertFilter,
  AdminReply, Notice, NoticeInput, NoticeRead, AdminNote, AdminNoteInput,
  DashboardData, CheckinRevision, MonthlyReportData,
  WorkerSignupInput, WorkerSignupRequest, ChangeAdminPasswordInput, RepairWorkerFieldsResult,
  PrivacyConsent, ResolveAttentionInput, MarkAttentionMonitoringInput,
  AttendanceRecord, AttendanceUpdateInput, AttendanceOverviewData, WorkerHomeData,
  AdminMessagesData, WorkerDetailData
} from './types';
import { getTodayString } from '../utils/date';
import { isWorkerEligibleForStatsOnDate, normalizeDateString } from '../utils/date';
import {
  getApiSessionToken,
  notifyApiUnauthorized,
  storeApiSession,
} from './sessionToken';

type AuthenticatedApiUser<T> = T & {
  session_token?: string;
  session_expires_at?: string;
};

function normalizeWorkerFromApi(worker: Worker): Worker {
  return {
    ...worker,
    employment_start_date: normalizeDateString(worker.employment_start_date || ''),
    employment_end_date: normalizeDateString(worker.employment_end_date || ''),
  };
}

function normalizeSignupRequestFromApi(request: WorkerSignupRequest): WorkerSignupRequest {
  return {
    ...request,
    employment_start_date: normalizeDateString(request.employment_start_date || ''),
  };
}

export class GoogleSheetsService implements DataService {
  private apiUrl: string;
  private inFlightReads = new Map<string, Promise<unknown>>();

  constructor(apiUrl: string) {
    // API URL이 제대로 뒤에 '/' 또는 슬래시 없이 연결되도록 포맷팅
    this.apiUrl = apiUrl.trim();
  }

  // 공통 POST 요청 헬퍼
  private async postRequest<T>(payload: any): Promise<T> {
    try {
      const sessionToken = getApiSessionToken();
      const requestPayload = sessionToken
        ? { ...payload, session_token: sessionToken }
        : payload;
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        // CORS Preflight를 피하기 위해 text/plain으로 JSON 문자열을 보냅니다.
        // Google Apps Script는 doPost의 e.postData.contents를 JSON.parse하기만 하면 됩니다.
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify(requestPayload),
        redirect: 'follow' // Apps Script 리다이렉트 처리를 위해 필수
      });

      if (!response.ok) {
        throw new Error(`HTTP API 오류: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'error') {
        if (result.code === 'unauthorized') notifyApiUnauthorized();
        const error = new Error(result.message || 'API 서버 오류가 발생했습니다.');
        (error as any).code = result.code;
        throw error;
      }
      return result.data as T;
    } catch (error) {
      console.error('Google Sheets API POST 실패:', error);
      throw error;
    }
  }

  // 공통 GET 요청 헬퍼
  private async getRequest<T>(action: string, params: Record<string, string> = {}): Promise<T> {
    const publicGetActions = new Set(['health_check']);
    if (!publicGetActions.has(action)) {
      const sortedParams = Object.keys(params).sort().reduce<Record<string, string>>((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {});
      const requestKey = `${action}:${JSON.stringify(sortedParams)}`;
      const existingRequest = this.inFlightReads.get(requestKey) as Promise<T> | undefined;
      if (existingRequest) return existingRequest;

      const request = this.postRequest<T>({ action, data: params }).finally(() => {
        if (this.inFlightReads.get(requestKey) === request) {
          this.inFlightReads.delete(requestKey);
        }
      });
      this.inFlightReads.set(requestKey, request);
      return request;
    }

    try {
      const urlParams = new URLSearchParams({
        action,
        ...params,
      });
      const separator = this.apiUrl.includes('?') ? '&' : '?';
      const requestUrl = `${this.apiUrl}${separator}${urlParams.toString()}`;

      const response = await fetch(requestUrl, {
        method: 'GET',
        redirect: 'follow'
      });

      if (!response.ok) {
        throw new Error(`HTTP API 오류: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'error') {
        if (result.code === 'unauthorized') notifyApiUnauthorized();
        const error = new Error(result.message || 'API 서버 오류가 발생했습니다.');
        (error as any).code = result.code;
        throw error;
      }
      return result.data as T;
    } catch (error) {
      console.error('Google Sheets API GET 실패:', error);
      throw error;
    }
  }

  // 인증
  async workerLogin(name: string, pin: string): Promise<Worker> {
    const response = await this.postRequest<AuthenticatedApiUser<Worker>>({
      action: 'worker_login',
      name,
      pin
    });
    if (response.session_token && response.session_expires_at) {
      storeApiSession(
        response.session_token,
        response.session_expires_at,
        'worker',
        response.worker_id
      );
    }
    const { session_token: _token, session_expires_at: _expiresAt, ...worker } = response;
    return worker;
  }

  async adminLogin(loginId: string, password: string): Promise<Admin | null> {
    const response = await this.postRequest<AuthenticatedApiUser<Admin>>({
      action: 'admin_login',
      loginId,
      password
    });
    if (response.session_token && response.session_expires_at) {
      storeApiSession(
        response.session_token,
        response.session_expires_at,
        'admin',
        response.admin_id
      );
    }
    const { session_token: _token, session_expires_at: _expiresAt, ...admin } = response;
    return admin;
  }

  // 취업자 관리
  async getWorkers(): Promise<Worker[]> {
    const workers = await this.getRequest<Worker[]>('get_workers');
    return workers.map(normalizeWorkerFromApi);
  }

  async getWorker(workerId: string): Promise<Worker | null> {
    try {
      const worker = await this.getRequest<Worker | null>('get_worker', { worker_id: workerId });
      return worker ? normalizeWorkerFromApi(worker) : null;
    } catch {
      const workers = await this.getWorkers();
      return workers.find(w => w.worker_id === workerId) || null;
    }
  }

  async createWorker(data: Omit<Worker, 'worker_id' | 'created_at' | 'updated_at'>): Promise<Worker> {
    const worker = await this.postRequest<Worker>({
      action: 'create_worker',
      data: {
        ...data,
        employment_start_date: normalizeDateString(data.employment_start_date || ''),
        employment_end_date: normalizeDateString(data.employment_end_date || ''),
        pin: (data as any).pin || data.pin_hash
      }
    });
    return normalizeWorkerFromApi(worker);
  }

  async updateWorker(workerId: string, data: Partial<Worker>): Promise<Worker> {
    const normalizedData = {
      ...data,
      ...(Object.prototype.hasOwnProperty.call(data, 'employment_start_date')
        ? { employment_start_date: normalizeDateString(data.employment_start_date || '') }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(data, 'employment_end_date')
        ? { employment_end_date: normalizeDateString(data.employment_end_date || '') }
        : {}),
    };
    const worker = await this.postRequest<Worker>({
      action: 'update_worker',
      data: {
        ...normalizedData,
        worker_id: workerId,
        pin: (data as any).pin || data.pin_hash
      }
    });
    return normalizeWorkerFromApi(worker);
  }

  async deleteWorker(workerId: string, terminationDate?: string, reason?: string): Promise<void> {
    const defaultDate = terminationDate || getTodayString();
    const defaultReason = reason || '퇴사';
    await this.terminateWorker(workerId, defaultDate, defaultReason);
  }

  async terminateWorker(workerId: string, terminationDate: string, reason: string): Promise<void> {
    try {
      await this.postRequest<void>({
        action: 'terminate_worker',
        data: {
          worker_id: workerId,
          employment_end_date: terminationDate,
          termination_reason: reason
        }
      });
    } catch (err) {
      console.warn('terminate_worker API 실패, updateWorker fallback 실행:', err);
      await this.updateWorker(workerId, {
        status: 'inactive',
        employment_status: '퇴사',
        employment_end_date: terminationDate,
        termination_reason: reason
      } as any);
    }
  }

  async createWorkerSignupRequest(data: WorkerSignupInput): Promise<WorkerSignupRequest> {
    const request = await this.postRequest<WorkerSignupRequest>({
      action: 'create_worker_signup_request',
      data: {
        ...data,
        employment_start_date: normalizeDateString(data.employment_start_date || '')
      }
    });
    return normalizeSignupRequestFromApi(request);
  }

  async getWorkerSignupRequests(status?: 'pending' | 'approved' | 'rejected'): Promise<WorkerSignupRequest[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    const requests = await this.getRequest<WorkerSignupRequest[]>('get_worker_signup_requests', params);
    return requests.map(normalizeSignupRequestFromApi);
  }

  async approveWorkerSignupRequest(requestId: string, adminId: string, adminMemo = ''): Promise<Worker> {
    const worker = await this.postRequest<Worker>({
      action: 'approve_worker_signup_request',
      data: { request_id: requestId, admin_id: adminId, admin_memo: adminMemo }
    });
    return normalizeWorkerFromApi(worker);
  }

  async rejectWorkerSignupRequest(requestId: string, adminId: string, adminMemo = ''): Promise<WorkerSignupRequest> {
    const request = await this.postRequest<WorkerSignupRequest>({
      action: 'reject_worker_signup_request',
      data: { request_id: requestId, admin_id: adminId, admin_memo: adminMemo }
    });
    return normalizeSignupRequestFromApi(request);
  }

  async checkWorkerSignupStatus(name: string, pin: string): Promise<WorkerSignupRequest | null> {
    try {
      const request = await this.postRequest<WorkerSignupRequest | null>({
        action: 'check_worker_signup_status',
        data: { name, pin }
      });
      return request ? normalizeSignupRequestFromApi(request) : null;
    } catch {
      return null;
    }
  }

  // 관리자 관리
  async getAdmins(): Promise<Admin[]> {
    return this.getRequest<Admin[]>('get_admins');
  }

  async createAdmin(_data: Omit<Admin, 'admin_id' | 'created_at' | 'updated_at'>): Promise<Admin> {
    // 스프레드시트 스크립트 코드상에는 create_admin 핸들러가 명시되지 않았으나, 필요 시 데이터 연계
    throw new Error('Apps Script에서 관리자 등록 기능은 제공되지 않습니다. 스프레드시트 초기화를 이용하세요.');
  }

  async updateAdmin(_adminId: string, _data: Partial<Admin>): Promise<Admin> {
    throw new Error('Apps Script에서 관리자 정보 변경 기능은 제공되지 않습니다.');
  }

  async changeAdminPassword(data: ChangeAdminPasswordInput): Promise<void> {
    await this.postRequest<void>({
      action: 'change_admin_password',
      data
    });
  }

  // 체크인
  async getCheckins(workerId: string, month?: string): Promise<DailyCheckin[]> {
    const params: Record<string, string> = {};
    if (workerId && workerId.trim()) params.worker_id = workerId;
    if (month) params.month = month;
    return this.getRequest<DailyCheckin[]>('get_checkins', params);
  }

  async getCheckinByDate(workerId: string, date: string): Promise<DailyCheckin | null> {
    const checkins = await this.getRequest<DailyCheckin[]>('get_checkins', { worker_id: workerId, date });
    return checkins.length > 0 ? checkins[0] : null;
  }

  async getTodayCheckin(workerId: string): Promise<DailyCheckin | null> {
    const today = getTodayString();
    return this.getCheckinByDate(workerId, today);
  }

  async createCheckin(data: CheckinInput): Promise<DailyCheckin> {
    return this.postRequest<DailyCheckin>({
      action: 'create_checkin',
      data
    });
  }

  async updateCheckin(checkinId: string, data: CheckinInput): Promise<DailyCheckin> {
    return this.postRequest<DailyCheckin>({
      action: 'update_checkin',
      data: { ...data, checkin_id: checkinId }
    });
  }

  async getCheckinRevisions(checkinId: string): Promise<CheckinRevision[]> {
    // Apps script revision 획득 API
    const revisions = await this.getRequest<CheckinRevision[]>('get_revisions', { checkin_id: checkinId });
    return revisions;
  }

  // 알림
  async getAlerts(filters?: AlertFilter): Promise<Alert[]> {
    let alerts = await this.getRequest<Alert[]>('get_alerts');
    if (filters) {
      if (filters.status) alerts = alerts.filter(a => a.status === filters.status);
      if (filters.worker_id) alerts = alerts.filter(a => a.worker_id === filters.worker_id);
    }
    return alerts;
  }

  async updateAlertStatus(alertId: string, status: 'acknowledged' | 'resolved', memo?: string): Promise<void> {
    await this.postRequest<void>({
      action: 'update_alert',
      data: { alert_id: alertId, status, admin_memo: memo }
    });
  }

  async resolveAttentionAlerts(data: ResolveAttentionInput): Promise<void> {
    await this.postRequest<void>({
      action: 'resolve_attention_alerts',
      data
    });
  }

  async markAttentionMonitoring(data: MarkAttentionMonitoringInput): Promise<void> {
    await this.postRequest<void>({
      action: 'mark_attention_monitoring',
      data
    });
  }

  // 답변
  async getReplies(workerId: string): Promise<AdminReply[]> {
    return this.getRequest<AdminReply[]>('get_replies', { worker_id: workerId });
  }

  async getUnrepliedMessages(): Promise<AdminReply[]> {
    const replies = await this.getRequest<AdminReply[]>('get_replies');
    return replies.filter(r =>
      r.worker_message &&
      r.worker_message.trim() !== '' &&
      (!r.reply_text || r.reply_text.trim() === '')
    );
  }

  async createReply(checkinId: string, adminId: string, replyText: string): Promise<AdminReply> {
    return this.postRequest<AdminReply>({
      action: 'create_reply',
      data: { checkin_id: checkinId, admin_id: adminId, reply_text: replyText }
    });
  }

  async markReplyRead(replyId: string): Promise<void> {
    await this.postRequest<void>({
      action: 'mark_reply_read',
      data: { reply_id: replyId }
    });
  }

  // 공지
  async getNotices(activeOnly?: boolean): Promise<Notice[]> {
    let notices = await this.getRequest<Notice[]>('get_notices');
    if (activeOnly) {
      const today = getTodayString();
      notices = notices.filter(n => n.is_visible && n.start_date <= today && n.end_date >= today);
    }
    return notices;
  }

  async createNotice(data: NoticeInput, adminId: string): Promise<Notice> {
    return this.postRequest<Notice>({
      action: 'create_notice',
      data: { ...data, admin_id: adminId }
    });
  }

  async updateNotice(noticeId: string, data: Partial<NoticeInput>): Promise<Notice> {
    return this.postRequest<Notice>({
      action: 'update_notice',
      data: { ...data, notice_id: noticeId }
    });
  }

  async getNoticeReads(noticeId: string): Promise<NoticeRead[]> {
    const params: Record<string, string> = {};
    if (noticeId && noticeId.trim()) params.notice_id = noticeId;
    return this.getRequest<NoticeRead[]>('get_notice_reads', params);
  }

  async recordNoticeRead(noticeId: string, userId: string, userType: string): Promise<void> {
    await this.postRequest<void>({
      action: 'record_notice_read',
      data: { notice_id: noticeId, user_id: userId, user_type: userType }
    });
  }

  // 상담 기록
  async getAdminNotes(workerId: string): Promise<AdminNote[]> {
    return this.getRequest<AdminNote[]>('get_admin_notes', { worker_id: workerId });
  }

  async createAdminNote(data: AdminNoteInput, adminId: string): Promise<AdminNote> {
    return this.postRequest<AdminNote>({
      action: 'create_admin_note',
      data: { ...data, admin_id: adminId }
    });
  }

  async getDashboardData(): Promise<DashboardData> {
    const data = await this.getRequest<any>('get_dashboard');
    return {
      totalWorkers: data.totalWorkers || 0,
      todayCheckedCount: data.todayCheckedCount || 0,
      todayUncheckedCount: data.todayUncheckedCount || 0,
      todayAlertCount: data.todayAlertCount || 0,
      unresolvedAlertCount: data.unresolvedAlertCount || 0,
      unansweredMessageCount: data.unansweredMessageCount || 0,
      unreadNoticeCount: data.unreadNoticeCount || 0,
      pendingSignupRequestCount: data.pendingSignupRequestCount || 0,
      attentionWorkers: data.attentionWorkers || [],
      unansweredMessages: data.unansweredMessages || [],
      recentAlerts: data.recentAlerts || [],
      uncheckedWorkers: data.uncheckedWorkers || [],
      recentWorkers: data.recentWorkers || [],
      workerNames: data.workerNames || {},
      todayCheckins: data.todayCheckins || []
    };
  }

  async getMonthlyReport(year: string, month: string): Promise<MonthlyReportData> {
    try {
      return await this.getRequest<MonthlyReportData>('get_monthly_report', { year, month });
    } catch {
      const [workers, checkins, alerts, replies, notes] = await Promise.all([
        this.getWorkers(),
        this.getCheckins(''),
        this.getAlerts(),
        this.getReplies(''),
        this.getAdminNotes('')
      ]);
      return this.buildMonthlyReport(year, month, workers, checkins, alerts, replies, notes);
    }
  }

  private buildMonthlyReport(
    year: string,
    month: string,
    workers: Worker[],
    checkins: DailyCheckin[],
    alerts: Alert[],
    replies: AdminReply[],
    notes: AdminNote[]
  ): MonthlyReportData {
    const now = new Date();
    const targetYear = year || String(now.getFullYear());
    const parsedMonth = Number.parseInt(month || String(now.getMonth() + 1), 10);
    const targetMonth = String(Number.isFinite(parsedMonth) ? parsedMonth : now.getMonth() + 1).padStart(2, '0');
    const monthPrefix = `${targetYear}-${targetMonth}`;
    const daysInMonth = new Date(Number(targetYear), Number(targetMonth), 0).getDate();

    const normalizeDate = (value: string) => normalizeDateString(value || '');
    const round1 = (value: number) => Math.round(value * 10) / 10;
    const average = (values: number[]) => values.length > 0 ? round1(values.reduce((sum, value) => sum + value, 0) / values.length) : 0;
    const checkinDateById = new Map(checkins.map((checkin) => [checkin.checkin_id, normalizeDate(checkin.checkin_date)]));
    const replyByCheckinId = new Map<string, AdminReply>();
    replies.forEach((reply) => {
      if (reply.checkin_id && !replyByCheckinId.has(reply.checkin_id)) {
        replyByCheckinId.set(reply.checkin_id, reply);
      }
    });
    const getReplyDate = (reply: AdminReply) => normalizeDate(checkinDateById.get(reply.checkin_id) || reply.created_at || reply.updated_at);

    const currentWorkers = workers.filter((worker) => worker.status === 'active' && worker.employment_status === '취업 중');
    const currentWorkerCount = currentWorkers.length;
    const workersMissingEmploymentStartDate = currentWorkers.filter((worker) => !worker.employment_start_date).length;
    const workerById = new Map(workers.map((worker) => [worker.worker_id, worker]));
    const monthlyCheckins = checkins.filter((checkin) => {
      const date = normalizeDate(checkin.checkin_date);
      const worker = workerById.get(checkin.worker_id);
      return date.startsWith(monthPrefix) && !!worker && isWorkerEligibleForStatsOnDate(worker, date);
    });
    const previousMonthDate = new Date(Number(targetYear), Number(targetMonth) - 2, 1);
    const previousMonthPrefix = `${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth() + 1).padStart(2, '0')}`;
    const previousMonthCheckins = checkins.filter((checkin) => {
      const date = normalizeDate(checkin.checkin_date);
      const worker = workerById.get(checkin.worker_id);
      return date.startsWith(previousMonthPrefix) && !!worker && isWorkerEligibleForStatsOnDate(worker, date);
    });
    const monthlyAlerts = alerts.filter((alert) => normalizeDate(alert.alert_date).startsWith(monthPrefix));
    const monthlyReplies = replies.filter((reply) => getReplyDate(reply).startsWith(monthPrefix));
    const monthlyNotes = notes.filter((note) => normalizeDate(note.note_date).startsWith(monthPrefix));
    const messageCheckins = monthlyCheckins.filter((checkin) => Boolean(checkin.message_to_admin?.trim()));
    const checkinsByDate = new Map<string, DailyCheckin[]>();
    monthlyCheckins.forEach((checkin) => {
      const date = normalizeDate(checkin.checkin_date);
      const dayCheckins = checkinsByDate.get(date);
      if (dayCheckins) dayCheckins.push(checkin);
      else checkinsByDate.set(date, [checkin]);
    });
    const alertsByDate = new Map<string, Alert[]>();
    const alertCountByType = new Map<string, number>();
    monthlyAlerts.forEach((alert) => {
      const date = normalizeDate(alert.alert_date);
      const dayAlerts = alertsByDate.get(date);
      if (dayAlerts) dayAlerts.push(alert);
      else alertsByDate.set(date, [alert]);
      alertCountByType.set(alert.alert_type, (alertCountByType.get(alert.alert_type) || 0) + 1);
    });

    const uniqueWorkers = new Set(monthlyCheckins.map((checkin) => checkin.worker_id)).size;
    const totalCheckins = monthlyCheckins.length;
    const averageScore = average(monthlyCheckins.map((checkin) => Number(checkin.average_score || 0)));
    const unansweredMessageCount = messageCheckins.filter((checkin) => {
      const reply = replyByCheckinId.get(checkin.checkin_id);
      return !reply || reply.message_status === '미확인' || !reply.reply_text?.trim();
    }).length;

    const questionLabels = {
      q1_mood: '오늘 기분은 어땠어요?',
      q2_work: '오늘 일은 어땠어요?',
      q3_relationship: '직장에서 사람들과 잘 지냈어요?',
      q4_health: '몸은 괜찮았어요?',
      q5_need_help: '도움이 필요하다고 느꼈나요?',
      q6_overall: '오늘 하루는 전체적으로 어땠어요?',
    };
    type QuestionKey = keyof typeof questionLabels;
    const questionKeys = Object.keys(questionLabels) as QuestionKey[];
    const questionAverages = questionKeys.map((key) => {
      const currentAverage = average(monthlyCheckins.map((checkin) => Number(checkin[key] || 0)));
      const previousAverage = average(previousMonthCheckins.map((checkin) => Number(checkin[key] || 0)));
      return {
        key,
        label: questionLabels[key],
        averageScore: currentAverage,
        ...(monthlyCheckins.length > 0 && previousMonthCheckins.length > 0
          ? { previousMonthDiff: round1(currentAverage - previousAverage) }
          : {}),
      };
    });

    const alertLabels = {
      score_drop: '전날 대비 2점 이상 하락',
      low_score: '평균 2.5 이하',
      consecutive_low: '2일 연속 낮은 점수',
      consecutive_drop: '3일 연속 하락',
      need_help: '도움 필요 문항 낮은 점수',
      has_message: '관리자에게 하고 싶은 말 작성',
    };
    const alertBreakdown = Object.entries(alertLabels).map(([type, label]) => ({
      type,
      label,
      count: alertCountByType.get(type) || 0,
    }));

    const dailyStats = Array.from({ length: daysInMonth }, (_, index) => {
      const day = String(index + 1).padStart(2, '0');
      const date = `${monthPrefix}-${day}`;
      const eligibleWorkers = workers.filter((worker) => isWorkerEligibleForStatsOnDate(worker, date));
      const dayCheckins = checkinsByDate.get(date) || [];
      const dayMessageCheckins = dayCheckins.filter((checkin) => Boolean(checkin.message_to_admin?.trim()));
      const completedCount = new Set(dayCheckins.map((checkin) => checkin.worker_id)).size;
      return {
        date,
        eligibleWorkerCount: eligibleWorkers.length,
        completedCount,
        missingCount: Math.max(0, eligibleWorkers.length - completedCount),
        averageScore: average(dayCheckins.map((checkin) => Number(checkin.average_score || 0))),
        alertCount: (alertsByDate.get(date) || []).length,
        messageCount: new Set(dayMessageCheckins.map((checkin) => checkin.worker_id)).size,
        unansweredCount: dayMessageCheckins.filter((checkin) => {
          const reply = replyByCheckinId.get(checkin.checkin_id);
          return !reply || reply.message_status === '미확인' || !reply.reply_text?.trim();
        }).length,
      };
    });
    const eligibleWorkerDays = dailyStats.reduce((sum, day) => sum + day.eligibleWorkerCount, 0);
    const completionRate = eligibleWorkerDays === 0 ? 0 : Math.min(100, round1((totalCheckins / eligibleWorkerDays) * 100));

    const lowestQuestion = totalCheckins > 0
      ? [...questionAverages].sort((a, b) => a.averageScore - b.averageScore)[0]
      : null;
    const observations = [
      `${targetYear}년 ${targetMonth}월 일일 체크에 참여한 취업자는 총 ${uniqueWorkers}명입니다.`,
      `월간 체크 연인원은 총 ${totalCheckins}건입니다.`,
      `월간 응답 대상 연인원은 총 ${eligibleWorkerDays}명일입니다.`,
      `월간 평균 점수는 ${averageScore}점입니다.`,
      `위험 알림은 총 ${monthlyAlerts.length}건 발생했습니다.`,
    ];
    if (lowestQuestion) observations.push(`가장 낮은 평균 점수를 보인 문항은 "${lowestQuestion.label}"입니다.`);

    const supportNeeds: string[] = [];
    if (monthlyAlerts.length > 0) supportNeeds.push('위험 알림이 발생한 취업자에 대한 개별 확인이 필요합니다.');
    if (unansweredMessageCount > 0) supportNeeds.push('미답변 메시지에 대한 관리자 확인 및 답변 처리가 필요합니다.');
    if (lowestQuestion?.key === 'q4_health') supportNeeds.push('건강 상태 관련 문항 점수가 낮아 근무 피로도와 건강 상태 확인이 필요합니다.');
    if (lowestQuestion?.key === 'q3_relationship') supportNeeds.push('직장 내 관계 적응에 대한 추가 확인이 필요합니다.');
    if (supportNeeds.length === 0) supportNeeds.push('이번 달에는 즉시 조치가 필요한 추가 지원 항목이 크게 확인되지 않았습니다.');

    return {
      year: targetYear,
      month: targetMonth,
      currentWorkerCount,
      eligibleWorkerDays,
      workersMissingEmploymentStartDate,
      uniqueWorkers,
      totalCheckins,
      completionRate,
      averageScore,
      alertCount: monthlyAlerts.length,
      unansweredMessageCount,
      workerMessageCount: messageCheckins.length,
      adminReplyCount: monthlyReplies.filter((reply) => Boolean(reply.reply_text?.trim())).length,
      workerReadReplyCount: monthlyReplies.filter((reply) => reply.message_status === '취업자읽음').length,
      closedMessageCount: monthlyReplies.filter((reply) => reply.message_status === '종결').length,
      adminNoteCount: monthlyNotes.length,
      dailyStats,
      questionAverages,
      alertBreakdown,
      observations,
      supportNeeds,
      generatedAt: new Date().toISOString(),
    };
  }

  async repairWorkerFields(dryRun = true): Promise<RepairWorkerFieldsResult> {
    return this.postRequest<RepairWorkerFieldsResult>({
      action: 'repair_worker_fields',
      data: { dryRun }
    });
  }

  async getPrivacyConsents(): Promise<PrivacyConsent[]> {
    return this.getRequest<PrivacyConsent[]>('get_privacy_consents');
  }

  async getWorkerHome(workerId: string): Promise<WorkerHomeData> {
    return this.getRequest<WorkerHomeData>('get_worker_home', { worker_id: workerId });
  }

  async getAdminMessages(): Promise<AdminMessagesData> {
    return this.getRequest<AdminMessagesData>('get_admin_messages');
  }

  async getWorkerDetail(workerId: string): Promise<WorkerDetailData> {
    return this.getRequest<WorkerDetailData>('get_worker_detail', { worker_id: workerId });
  }

  async getTodayAttendance(workerId: string): Promise<AttendanceRecord | null> {
    return this.getRequest<AttendanceRecord | null>('get_today_attendance', { worker_id: workerId });
  }

  async getAttendance(workerId?: string, month?: string, date?: string): Promise<AttendanceRecord[]> {
    const params: Record<string, string> = {};
    if (workerId) params.worker_id = workerId;
    if (month) params.month = month;
    if (date) params.date = date;
    return this.getRequest<AttendanceRecord[]>('get_attendance', params);
  }

  async getAttendanceOverview(date: string, search?: string, status?: string): Promise<AttendanceOverviewData> {
    const params: Record<string, string> = { date };
    if (search) params.search = search;
    if (status && status !== 'all') params.status = status;
    return this.getRequest<AttendanceOverviewData>('get_attendance_overview', params);
  }

  async clockIn(workerId: string, workerMemo?: string): Promise<AttendanceRecord> {
    return this.postRequest<AttendanceRecord>({
      action: 'clock_in',
      data: { worker_id: workerId, worker_memo: workerMemo || '' }
    });
  }

  async clockOut(workerId: string, workerMemo?: string): Promise<AttendanceRecord> {
    return this.postRequest<AttendanceRecord>({
      action: 'clock_out',
      data: { worker_id: workerId, worker_memo: workerMemo || '' }
    });
  }

  async updateAttendance(attendanceId: string, data: AttendanceUpdateInput): Promise<AttendanceRecord> {
    return this.postRequest<AttendanceRecord>({
      action: 'update_attendance',
      data: { attendance_id: attendanceId, ...data }
    });
  }
}
